import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Landing from "../landing/Landing";
import { Modal, PrimaryButton, SuccessButton, SecondaryButton } from "../../design-system";
import { typography, surfaces, borders, textColors } from "../../design-system/tokens";
import ConfettiOverlay from "../../design-system/components/ConfettiOverlay";
import { apiFetch } from "../../core/utils/api";
import { AuthContext } from "../../core/context/AuthContext";
import { buildReferralSearchParams, clearReferralCoupon, setReferralCoupon } from "../../core/utils/referralCoupon";

const formatCouponHeadline = (coupon) => {
  if (!coupon) return null;
  if (coupon.discount_type === "percent") return `${coupon.percent_off}% off`;
  if (coupon.discount_type === "amount") return `${coupon.amount_off} ${coupon.currency || "USD"} off`;
  if (coupon.discount_type === "trial") return `${coupon.free_trial_days || 0} trial days`;
  return "Discount";
};

const formatDateShort = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

// Legacy /discount/<username> links: resolve the affiliate's promoted coupon code.
// Returns "" when the username doesn't resolve or no coupon is promoted.
const resolvePromotedCouponCode = async (username) => {
  try {
    const resp = await apiFetch(`/api/discount/${encodeURIComponent(username)}/`, {
      headers: { "Content-Type": "application/json" },
    });
    if (!resp.ok) return "";
    const json = await resp.json().catch(() => null);
    return String(json?.coupon?.code || "").trim().toUpperCase();
  } catch {
    return "";
  }
};

const CouponLanding = () => {
  const { slug: slugParam } = useParams();
  // The slug is first tried as a coupon code (canonically uppercase); the
  // affiliate-username fallback keeps the original casing.
  const slug = String(slugParam || "").trim();
  const code = slug.toUpperCase();
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);
  const isLoggedIn = Boolean(auth && auth.userId);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  // { coupon, influencer } — "influencer" is the frozen JSON key of the public
  // coupon endpoint response; it holds the affiliate who promotes the coupon.
  const [data, setData] = useState(null);
  const [modalOpen, setModalOpen] = useState(true);
  const [showCelebration, setShowCelebration] = useState(true);
  const [activating, setActivating] = useState(false);
  const [activationError, setActivationError] = useState(null);
  const [accessGrant, setAccessGrant] = useState(null); // { code, plan_name, ends_at }

  useEffect(() => {
    if (!showCelebration) return undefined;
    const t = setTimeout(() => setShowCelebration(false), 2500);
    return () => clearTimeout(t);
  }, [showCelebration]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const resp = await apiFetch(`/api/coupon/${encodeURIComponent(code)}/`, {
          headers: { "Content-Type": "application/json" },
        });
        const json = await resp.json().catch(() => null);
        if (resp.status === 404) {
          // Fallback: the slug may be an affiliate username from an old
          // /discount/<username> link — resolve their promoted coupon.
          const promotedCode = await resolvePromotedCouponCode(slug);
          if (cancelled) return;
          if (promotedCode && promotedCode !== code) {
            // Normalize the URL to the coupon code. The `!== code` guard avoids a
            // redirect loop when the promoted code itself 404s on the coupon lookup.
            navigate(`/discount/${encodeURIComponent(promotedCode)}`, { replace: true });
            return;
          }
          setNotFound(true);
          return;
        }
        if (!resp.ok) throw new Error(json?.error || "Failed to load coupon.");
        if (!cancelled) setData(json);
        const couponCode = String(json?.coupon?.code || code).trim();
        if (couponCode) {
          setReferralCoupon({ code: couponCode, affiliateUsername: json?.influencer?.username || null });
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load coupon.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug, code, navigate]);

  const referral = useMemo(() => {
    const c = String(data?.coupon?.code || "").trim();
    if (!c) return null;
    return { code: c, affiliateUsername: data?.influencer?.username || null };
  }, [data]);

  const referralSearch = useMemo(() => (referral ? buildReferralSearchParams(referral) : ""), [referral]);

  const coupon = data?.coupon || null;
  const headline = formatCouponHeadline(coupon);
  const freeAccess = coupon?.free_access || null;

  const activateFreeAccess = async () => {
    setActivating(true);
    setActivationError(null);
    try {
      const resp = await apiFetch("/api/coupons/free-access/redeem/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: coupon?.code || code }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error || "Failed to activate free access.");
      setAccessGrant(json?.access_grant || { code: coupon?.code || code });
      setShowCelebration(true);
    } catch (e) {
      setActivationError(e?.message || "Failed to activate free access.");
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="relative">
      <Landing />
      <ConfettiOverlay isActive={showCelebration && !notFound && !error} />

      {/* Coupon modal (shown first) */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="myvitae.ai"
        titleAlign="center"
        size="sm"
        closeOnOverlayClick
        closeOnEsc
        showCloseButton
        footer={
          <div className="w-full">
            {isLoggedIn ? (
              <div className="flex flex-col gap-3 w-full">
                {coupon && freeAccess && !accessGrant ? (
                  <SuccessButton type="button" fullWidth loading={activating} onClick={activateFreeAccess}>
                    Activate free access
                  </SuccessButton>
                ) : null}
                <PrimaryButton type="button" fullWidth onClick={() => setModalOpen(false)}>
                  Continue
                </PrimaryButton>
              </div>
            ) : (
              <div className="flex flex-col gap-3 w-full">
                <SuccessButton
                  type="button"
                  fullWidth
                  onClick={() => {
                    window.location.href = `/edit/register${referralSearch}`;
                  }}
                >
                  Register
                </SuccessButton>
                <PrimaryButton
                  type="button"
                  fullWidth
                  onClick={() => {
                    clearReferralCoupon();
                    window.location.href = `/edit/login${referralSearch}`;
                  }}
                >
                  Login
                </PrimaryButton>
                {coupon ? (
                  <SecondaryButton
                    type="button"
                    fullWidth
                    onClick={() => {
                      clearReferralCoupon();
                      setData((prev) => (prev ? { ...prev, coupon: null } : prev));
                      setModalOpen(false);
                    }}
                  >
                    Clear coupon
                  </SecondaryButton>
                ) : null}
              </div>
            )}
          </div>
        }
      >
        {loading ? (
          <div className={`${typography.bodySmall} ${textColors.secondary}`}>Loading…</div>
        ) : notFound ? (
          <div className="space-y-3">
            <div className={`${typography.h4} ${textColors.primary}`}>Coupon not found</div>
            <div className={`${typography.bodySmall} ${textColors.secondary}`}>
              We couldn’t find a coupon with the code{" "}
              <span className={`${textColors.primary} font-medium`}>{code}</span>. It may have expired or the link may be
              incorrect — you can still explore myvitae.ai below.
            </div>
          </div>
        ) : error ? (
          <div className={`rounded-lg border ${borders.default} ${surfaces.primary} p-3`}>
            <div className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>{error}</div>
          </div>
        ) : !coupon ? (
          <div className={`${typography.bodySmall} ${textColors.secondary}`}>
            No coupon is applied. You can keep browsing below.
          </div>
        ) : accessGrant ? (
          <div className="space-y-3">
            <div className={`${typography.h4} ${textColors.primary}`}>Free access activated!</div>
            <div className={`${typography.bodySmall} ${textColors.secondary}`}>
              You now have free access to the{" "}
              <span className={`${textColors.primary} font-medium`}>
                {(accessGrant.plan_name || freeAccess?.plan || "").toUpperCase() || "your"}
              </span>{" "}
              plan
              {formatDateShort(accessGrant.ends_at) ? (
                <>
                  {" "}until{" "}
                  <span className={`${textColors.primary} font-medium`}>{formatDateShort(accessGrant.ends_at)}</span>
                </>
              ) : null}
              .
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className={`${typography.h4} ${textColors.primary}`}>
              {freeAccess ? "Free access unlocked!" : "Discount applied automatically!"}
            </div>
            {data?.influencer?.username ? (
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                You’re visiting from{" "}
                <span className={`${textColors.primary} font-medium`}>@{data.influencer.username}</span>.
              </div>
            ) : null}
            <div className={`rounded-lg border ${borders.default} ${surfaces.secondary} p-3`}>
              <div className={`${typography.caption} ${textColors.tertiary}`}>Coupon</div>
              <div className={`${typography.h5} ${textColors.primary}`}>{coupon.code}</div>
              {freeAccess ? (
                <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                  Free {String(freeAccess.plan || "").toUpperCase()} plan
                  {formatDateShort(freeAccess.until) ? ` until ${formatDateShort(freeAccess.until)}` : ""}
                </div>
              ) : headline ? (
                <div className={`${typography.bodySmall} ${textColors.secondary}`}>{headline}</div>
              ) : null}
              {!freeAccess && coupon.include_renewals === false ? (
                <div className={`${typography.caption} ${textColors.tertiary} mt-1`}>Applies to the first invoice only.</div>
              ) : null}
            </div>
            {activationError ? (
              <div className={`rounded-lg border ${borders.default} ${surfaces.primary} p-3`}>
                <div className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>{activationError}</div>
              </div>
            ) : null}
            {isLoggedIn ? (
              freeAccess ? (
                <div className={`${typography.bodyLarge} ${textColors.primary}`}>
                  You’re logged in — activate your free access below!
                </div>
              ) : (
                <div className={`${typography.bodyLarge} ${textColors.primary}`}>
                  You’re logged in — this coupon is saved and automatically applied when you upgrade!
                </div>
              )
            ) : (
              <div className={`${typography.bodyLarge} ${textColors.primary}`}>
                If you sign up or log in from this page, we’ll keep this coupon ready for your upgrade.
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CouponLanding;
